/** @format */

import "./Header.scss";
import React, { FC, useState } from "react";
import LocaleSwitch from "../i18n/LocaleSwitch";
import { ReactComponent as BLLogoCy } from "./../../assets/images/dysgu-cyfunol.svg";
import { ReactComponent as BLLogoEn } from "./../../assets/images/blended-learning.svg";
import { ReactComponent as BLLogoNoText } from "./../../assets/images/blended-learning-no-text.svg";
import { ReactComponent as BLPlusLogoCy } from "./../../assets/images/dysgu-cyfunol-plws.svg";
import { ReactComponent as BLPlusLogoEn } from "./../../assets/images/blended-learning-plus.svg";
import { ReactComponent as BLPlusLogoEnNoText } from "./../../assets/images/blended-learning-plus-no-text.svg";
import { ReactComponent as WJECLogo } from "./../../assets/images/wjecLogo.svg";
import { ReactComponent as CBACLogo } from "./../../assets/images/cbacLogo.svg";
import { ReactComponent as EduqasLogo } from "./../../assets/images/eduqasLogo.svg";
import { ReactComponent as Burger } from "./../../assets/icons/list.svg";
import { ReactComponent as AdnoddLogo } from "./../../assets/images/adnodd-colour.svg";
import { ReactComponent as AdnoddLogoMobile } from "./../../assets/images/adnodd-mobile.svg";
import { ReactComponent as WelshGovLogo } from "./../../assets/images/welshGovLogo.svg";
import { ReactComponent as InfoCircle } from "bootstrap-icons/icons/info-circle.svg";
import { ReactComponent as X } from "bootstrap-icons/icons/x.svg";
import { Link, useLocation } from "react-router-dom";
import { selectShowSidebar, setShowSidebar } from "../sidebar/sidebarSlice";
import { useAppSelector } from "../../app/hooks";
import { useDispatch } from "react-redux";
import ThemeSwitcher from "../theme/ThemeSwitcher";
import EditableSwitch from "../editable/EditableSwitch";
import { useParams } from "react-router-dom";
import { selectIsPrintable } from "../printable/printableSlice";
import { Alert, Navbar } from "react-bootstrap";
import { useGetResourceQuery } from "../../app/services/resource/resourcesApi";
import { useGetUserResourceQuery } from "../../app/services/userResource/userResourcesApi";
import { selectIsEditable } from "../editable/editableSlice";
import EditResourceModal from "../editable/EditResourceModal";
import AuthFlag from "../../auth/AuthFlag";
import UserWidget from "../user/UserWidget";
import { Locale } from "../i18n/init";
import { useTranslation } from "react-i18next";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  MaintenancePeriod,
  useGetNextMaintenanceQuery,
} from "../../app/services/adminApi";

const MaintenanceNotification: FC<{ maintenancePeriod: MaintenancePeriod }> = ({
  maintenancePeriod,
}) => {
  const { t: _ } = useTranslation();

  const [isVisible, setIsVisible] = useState<boolean>(true);

  const formatDate = (date: number) =>
    new Date(date).toLocaleString("en-GB", {
      dateStyle: "short",
      timeStyle: "short",
    });

  if (isVisible)
    return (
      <Alert
        variant="warning"
        className="small"
        data-testid="maintenanceNotification"
      >
        <button
          type="button"
          className="btn-close position-absolute top-0 end-0 mt-2 me-2 p-2"
          aria-label="Close"
          onClick={() => {
            setIsVisible(false);
          }}
        />
        <div className="pe-4">
          {_("maintenanceNotification", {
            startDate: formatDate(maintenancePeriod.startDate),
            endDate: formatDate(maintenancePeriod.endDate),
          })}
        </div>
      </Alert>
    );

  return <></>;
};

const UserHeader: React.FC<{}> = () => {
  const { resourceId, userId, copy } = useParams();

  // HACK: this is a workaround for header not being used inside a route with i18n (I think?). A better fix would be to use Header not in App but just the components (inside routes) it is needed
  const location = useLocation();
  const locale = location.pathname.split("/")[1] as Locale;
  const { t: _ } = useTranslation();

  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);

  const dispatch = useDispatch();
  const showSidebar = useAppSelector(selectShowSidebar());
  const isEditable = useAppSelector(selectIsEditable());
  const isPrintable = useAppSelector(selectIsPrintable());

  const { data: resource, isLoading } = useGetUserResourceQuery(
    resourceId === undefined || copy === undefined || userId === undefined
      ? skipToken
      : {
          code: resourceId,
          locale: locale,
          copy: copy,
          email: userId,
        },
  );

  const { data: maintenance } = useGetNextMaintenanceQuery();

  return (
    <div id="app-header">
      {!isLoading && (
        <div className="w-100 bg-white top-0 left-0 right-0">
          <Navbar className="justify-content-normal navbar px-2 px-sm-3 py-1 d-flex align-items-center">
            <div className="me-lg-4">
              {locale === "en" && (
                <>
                  <BLPlusLogoEn
                    className="blLogo d-none d-sm-block"
                    aria-label="BL Plus logo En"
                  />
                  <BLPlusLogoEnNoText
                    className="blLogo d-block d-sm-none"
                    aria-label="BL Plus logo En no text"
                  />
                </>
              )}
              {locale === "cy" && (
                <>
                  <BLPlusLogoCy
                    className="blLogo d-none d-sm-block"
                    aria-label="BL Plus logo Cy"
                  />
                  <BLPlusLogoEnNoText
                    className="blLogo d-block d-sm-none"
                    aria-label="BL Plus logo Cy no text"
                  />
                </>
              )}
            </div>

            {!isPrintable && resource && (
              <>
                <div
                  role="button"
                  aria-label="sidebar-toggle"
                  className="sidebar-toggle p-2 p-sm-3 ms-1 ms-sm-3"
                  onClick={() => {
                    dispatch(setShowSidebar(!showSidebar));
                  }}
                >
                  <Burger />
                </div>
              </>
            )}
            <div className="ms-auto d-flex flex-row align-items-center">
              <div>
                <ThemeSwitcher />
              </div>
              {resource?.Branding === "eduqas" && locale === "en" && (
                <>
                  <EduqasLogo className="logo ms-0 ms-sm-3" />
                </>
              )}

              <UserWidget />

              {resource?.Object?.fundedBy === "WelshGov" &&
                resource?.Branding !== "eduqas" && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.gov.wales/"
                  >
                    <WelshGovLogo className="logo" />
                  </a>
                )}
              {resource?.Object?.fundedBy === "Adnodd" &&
                resource?.Branding !== "eduqas" && (
                  <a
                    target="_blank"
                    href={
                      locale === "en"
                        ? "https://adnodd.gov.wales/"
                        : "https://adnodd.llyw.cymru/"
                    }
                    rel="noreferrer"
                  >
                    <AdnoddLogo className="logo d-none d-sm-block" />
                    <AdnoddLogoMobile className="logo d-block d-sm-none" />
                  </a>
                )}

              {locale === "en" && <WJECLogo className="logo ms-0 ms-sm-3" />}
              {locale === "cy" && <CBACLogo className="logo ms-0 ms-sm-3" />}
            </div>
          </Navbar>
          {!isPrintable && resource && (
            <>
              {showDisclaimer && (
                <div
                  className="user-resource-info banner-info d-flex flex-row align-items-center gap-2 py-2 px-2 px-sm-3 container-fluid"
                  style={{ minHeight: "calc(2rem)" }}
                >
                  <InfoCircle className="flex-shrink-0" />
                  <p className="m-0">
                    <small>
                      {_("duplicate-resource-disclaimer")}
                      &nbsp;
                      <Link to={`/${resource.Code}`}>{_("click here")}</Link>.
                    </small>
                  </p>
                  <X
                    className="ms-auto flex-shrink-0 fs-3"
                    style={{ width: "24px", height: "24px", cursor: "pointer" }}
                    onClick={() => setShowDisclaimer(false)}
                  />
                </div>
              )}
              {isEditable && <EditableSwitch resource={resource} />}
            </>
          )}

          {maintenance?.nextMaintenance && (
            <MaintenanceNotification
              maintenancePeriod={maintenance.nextMaintenance}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ResourceHeader: React.FC = () => {
  const { resourceId } = useParams();
  const { data: resource, isLoading } = useGetResourceQuery(
    resourceId ?? skipToken,
  );

  // HACK: this is a workaround for header not being used inside a route with i18n (I think?). A better fix would be to use Header not in App but just the components (inside routes) it is needed
  const location = useLocation();
  const locale = location.pathname.split("/")[1] as Locale;

  const dispatch = useDispatch();
  const showSidebar = useAppSelector(selectShowSidebar());
  const isEditable = useAppSelector(selectIsEditable());
  const isPrintable = useAppSelector(selectIsPrintable());

  const { data: maintenance } = useGetNextMaintenanceQuery();

  return (
    <div id="app-header">
      {!isLoading && (
        <div className="w-100 bg-white top-0 left-0 right-0">
          <Navbar className="justify-content-normal navbar px-2 px-sm-3 py-1 d-flex align-items-center">
            <div className="me-lg-4">
              {locale === "en" && (
                <>
                  <BLLogoEn
                    className="blLogo d-none d-sm-block"
                    aria-label="BL logo En"
                  />
                  <BLLogoNoText
                    className="blLogo d-block d-sm-none"
                    aria-label="BL logo no text"
                  />
                </>
              )}
              {locale === "cy" && (
                <>
                  <BLLogoCy
                    className="blLogo d-none d-sm-block"
                    aria-label="BL logo Cy"
                  />
                  <BLLogoNoText
                    className="blLogo d-block d-sm-none"
                    aria-label="BL logo no text"
                  />
                </>
              )}
            </div>

            {!isPrintable && resource && (
              <>
                <div
                  role="button"
                  aria-label="sidebar-toggle"
                  className="sidebar-toggle p-2 p-sm-3 ms-1 ms-sm-3"
                  onClick={() => {
                    dispatch(setShowSidebar(!showSidebar));
                  }}
                >
                  <Burger />
                </div>
              </>
            )}
            <div className="ms-auto d-flex flex-row align-items-center">
              <div>
                <ThemeSwitcher />
              </div>
              {resource?.Branding === "eduqas" && locale === "en" && (
                <>
                  <EduqasLogo className="logo ms-0 ms-sm-3" />
                </>
              )}

              {resource?.Branding !== "eduqas" &&
                !["wjec", "cbac"].includes(resource?.Branding ?? "") && (
                  // margin to stop overlapping wjec logo when no user widget
                  <div className="me-2 me-sm-0">
                    <LocaleSwitch />
                  </div>
                )}

              <UserWidget />

              {resource?.Object?.fundedBy === "WelshGov" &&
                resource?.Branding !== "eduqas" && (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://www.gov.wales/"
                  >
                    <WelshGovLogo className="logo" />
                  </a>
                )}
              {resource?.Object?.fundedBy === "Adnodd" &&
                resource?.Branding !== "eduqas" && (
                  <a
                    target="_blank"
                    href={
                      locale === "en"
                        ? "https://adnodd.gov.wales/"
                        : "https://adnodd.llyw.cymru/"
                    }
                    rel="noreferrer"
                  >
                    <AdnoddLogo className="logo d-none d-sm-block" />
                    <AdnoddLogoMobile className="logo d-block d-sm-none" />
                  </a>
                )}

              {locale === "en" && <WJECLogo className="logo ms-0 ms-sm-3" />}
              {locale === "cy" && <CBACLogo className="logo ms-0 ms-sm-3" />}
            </div>
          </Navbar>

          {!isPrintable && resource && (
            <AuthFlag roles={"teacher"}>
              <div
                className={`py-2 px-3 editable-header container-fluid isReading }`}
              >
                <div
                  className="d-flex justify-content-end align-items-center"
                  style={{ height: "calc(1.5rem)" }}
                >
                  <EditResourceModal resource={resource} />
                </div>
              </div>
            </AuthFlag>
          )}
          {!isPrintable && resource && isEditable && (
            <EditableSwitch resource={resource} />
          )}

          {maintenance?.nextMaintenance && (
            <MaintenanceNotification
              maintenancePeriod={maintenance.nextMaintenance}
            />
          )}
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const { userId } = useParams();

  if (userId) {
    return <UserHeader />;
  } else {
    return <ResourceHeader />;
  }
};

export default Header;
